/**
 * Login — returning-user passkey assertion flow (Phase 2 #4b).
 *
 * The user types their email, the browser prompts for a registered passkey,
 * and on success we persist the session token + redirect to /. New users land
 * on /sign-up instead (registration flow); a link at the bottom routes them
 * there.
 */

import { authenticatePasskey, isPasskeySupported } from "@re-platform/auth";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/types";
import { Globe, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { APP_NAME } from "../../brand";
import { loadOnboardingAgent } from "../home/onboardingAgent";
import { type AuthNavState } from "./AuthBrand";
import { getAuth0Client, isAuth0Configured } from "./auth0Client";
import {
  passkeyApi,
  TenantAmbiguousError,
  type TenantChoice,
} from "./passkeyApi";
import { saveSession } from "./session";
import "./SignIn.css";
import { PublicFooter } from "../../shell/PublicFooter";

type Status =
  | { kind: "idle" }
  | { kind: "loading"; stage: "begin" | "prompt" | "finish" }
  | { kind: "error"; message: string };

export function Login() {
  const [email, setEmail] = useState("");
  const [supported, setSupported] = useState<boolean | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  // Set when the email is a client in >1 brokerage (Finding 3).
  const [ambiguous, setAmbiguous] = useState<TenantChoice[] | null>(null);
  //: Device-link recovery ("no passkey here?") — its own state so it never
  //: fights the sign-in button's status.
  const [sendingDeviceLink, setSendingDeviceLink] = useState(false);
  const [deviceLinkSent, setDeviceLinkSent] = useState(false);
  const navigate = useNavigate();
  // Pre-populated from /join or /b/:slug landing pages; router state is
  // ephemeral, so fall back to the persisted invite context (same rationale
  // as SignIn — Checkpoint #9 finding).
  const location = useLocation();
  const navState = location.state as AuthNavState | null;
  const presetTenantId =
    navState?.tenantId ?? loadOnboardingAgent()?.agentTenantId;
  useEffect(() => {
    setSupported(isPasskeySupported());
  }, []);

  async function handleAuth0Login(): Promise<void> {
    // Universal Login redirect — page navigates away. Cognito creds + session
    // get persisted by /auth/callback when Auth0 redirects back. The button
    // hides entirely if Auth0 isn't configured for this build, so missing
    // env vars never reach loginWithRedirect.
    try {
      await getAuth0Client().loginWithRedirect();
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Auth0 sign-in failed",
      });
    }
  }

  async function run(tenantId?: string): Promise<void> {
    setAmbiguous(null);
    setStatus({ kind: "loading", stage: "begin" });
    try {
      const begin = await passkeyApi.loginBegin({
        email: email.trim(),
        tenant_id: tenantId,
      });

      setStatus({ kind: "loading", stage: "prompt" });
      const result = await authenticatePasskey(
        begin.public_key as unknown as PublicKeyCredentialRequestOptionsJSON,
      );

      setStatus({ kind: "loading", stage: "finish" });
      const finish = await passkeyApi.loginFinish({
        challenge_id: begin.challenge_id,
        assertion: result.response as unknown as Record<string, unknown>,
      });

      saveSession({
        accessToken: finish.access_token,
        refreshToken: finish.refresh_token,
        expiresIn: finish.expires_in,
        userId: finish.user_id,
        tenantId: finish.tenant_id,
        primaryRole: finish.primary_role,
        email: finish.email,
        displayName: finish.display_name,
      });
      navigate("/");
    } catch (err) {
      if (err instanceof TenantAmbiguousError) {
        setAmbiguous(err.candidates);
        setStatus({ kind: "idle" });
        return;
      }
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Sign-in failed",
      });
    }
  }

  /**
   * Email a link to add THIS device to an existing account.
   *
   * The response is deliberately identical whether or not the account exists,
   * so the UI says the same thing either way — the only branch is a
   * multi-brokerage email, which surfaces the same picker sign-in does.
   */
  async function requestDeviceLink(): Promise<void> {
    const address = email.trim();
    if (!address) {
      setStatus({
        kind: "error",
        message: "Enter your email address first, then ask for the link.",
      });
      return;
    }
    setSendingDeviceLink(true);
    setStatus({ kind: "idle" });
    try {
      await passkeyApi.requestDeviceLink({
        email: address,
        ...(presetTenantId ? { tenant_id: presetTenantId } : {}),
      });
      setDeviceLinkSent(true);
    } catch (err) {
      if (err instanceof TenantAmbiguousError) {
        setAmbiguous(err.candidates);
      } else {
        setStatus({
          kind: "error",
          message:
            err instanceof Error ? err.message : "Couldn't send that link.",
        });
      }
    } finally {
      setSendingDeviceLink(false);
    }
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    await run(presetTenantId);
  }

  if (supported === false) {
    return (
      <main className="signin">
        <section className="signin__card">
          <h1>Passkeys aren't supported here</h1>
          <p>
            Your browser doesn't support WebAuthn passkeys yet. Try the latest
            Chrome, Safari, Firefox, or Edge on a device with Touch ID, Face ID,
            Windows Hello, or a security key.
          </p>
        </section>
        <PublicFooter />
      </main>
    );
  }

  return (
    <main className="signin">
      <section className="signin__card signin__card--split">
        <aside className="signin__aside">
          <div className="signin__aside-brand">
            <span className="signin__aside-mark">
              <ShieldCheck size={18} aria-hidden />
            </span>
            {APP_NAME}
          </div>
          <div>
            <div className="signin__aside-title">Welcome back</div>
            <p className="signin__aside-sub">
              Sign in with the passkey on this device — no password to type.
            </p>
          </div>
          <div className="signin__aside-note">
            <ShieldCheck size={15} aria-hidden /> Passkeys, not passwords
          </div>
        </aside>

        <div className="signin__main">
          <div className="signin__eyebrow">Welcome back</div>
          <h1>Sign in to your account</h1>
          {new URLSearchParams(location.search).get("reason") === "expired" && (
            <div
              className="signin__error"
              role="alert"
              style={{ marginBottom: 12 }}
            >
              <strong>Your session expired.</strong>
              <p>Please sign in again to continue.</p>
            </div>
          )}
          <p className="signin__subtitle">
            Sign in with the passkey you registered on this device — no password
            to type.
          </p>

          <form className="signin__form" onSubmit={handleSubmit}>
            <label className="signin__field">
              <span>Email address</span>
              <input
                type="email"
                required
                autoComplete="username webauthn"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status.kind === "loading"}
              />
            </label>

            <button
              type="submit"
              className="signin__cta rp-btn rp-btn--primary"
              disabled={status.kind === "loading" || !email.trim()}
            >
              {status.kind === "loading" ? (
                <>
                  <Loader2 className="signin__spin" size={18} aria-hidden />
                  {status.stage === "begin" && "Looking up your passkey…"}
                  {status.stage === "prompt" && "Confirm with your device…"}
                  {status.stage === "finish" && "Signing you in…"}
                </>
              ) : (
                <>
                  <ShieldCheck size={18} aria-hidden />
                  Continue with passkey
                </>
              )}
            </button>
          </form>

          {ambiguous && (
            <div
              className="signin__choices"
              role="group"
              aria-label="Choose your brokerage"
            >
              <p className="signin__subtitle">
                This email is registered with more than one brokerage. Which
                one?
              </p>
              {ambiguous.map((choice) => (
                <button
                  key={choice.tenant_id}
                  type="button"
                  className="signin__choice rp-btn rp-btn--secondary"
                  onClick={() => void run(choice.tenant_id)}
                >
                  {choice.tenant_name}
                </button>
              ))}
            </div>
          )}

          {status.kind === "error" && (
            <div className="signin__error" role="alert">
              <strong>Something went wrong.</strong>
              <p>{status.message}</p>
            </div>
          )}

          {isAuth0Configured() && (
            <button
              type="button"
              className="signin__cta rp-btn"
              onClick={() => void handleAuth0Login()}
              disabled={status.kind === "loading"}
              style={{ marginTop: 12 }}
            >
              <Globe size={18} aria-hidden />
              Continue with Auth0
            </button>
          )}

          {/* The recovery path for the go-live blocker: a client whose passkey
            lives on a device they no longer have (or never had — signed up on
            a laptop, now holding their phone) had NO way in at all. */}
          {deviceLinkSent ? (
            <p className="signin__footnote" role="status">
              If that address has an account, we've emailed it a link to add
              this device. It works once and expires in 15 minutes.
            </p>
          ) : (
            <p className="signin__footnote">
              <button
                type="button"
                className="signin__linkbtn"
                disabled={status.kind === "loading" || sendingDeviceLink}
                onClick={() => void requestDeviceLink()}
              >
                {sendingDeviceLink
                  ? "Sending…"
                  : "No passkey on this device? Email me a link"}
              </button>
            </p>
          )}

          <p className="signin__footnote">
            New here? <Link to="/sign-up">Create an account</Link>.
          </p>
        </div>
      </section>
      <PublicFooter />
    </main>
  );
}
